<template>
  <div>
    <label id="listbox-label"> {{ label }} </label>
    <div
      class="select-container"
      v-clickAway="{
        exclude: ['exclude', 'searchInput'],
        handler: 'closeUserList',
      }"
    >
      <button
        @click="toggleUserList"
        type="button"
        aria-haspopup="listbox"
        aria-expanded="true"
        aria-labelledby="listbox-label"
      >
        <span v-show="showList" class="list-title">{{
          selectedUsersString
        }}</span>
        <input
          v-show="showList"
          ref="searchInput"
          :class="['selected-users', { 'pointer-events-none': !showList }]"
          v-model="searchUsers"
          placeholder="Search users..."
        />
        <input
          v-show="!showList"
          class="selected-users"
          readonly
          v-model="selectedUsersString"
        />

        <span class="arrows">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </span>
      </button>
      <ul
        v-if="showList"
        class="users-list"
        role="listbox"
        aria-labelledby="listbox-label"
        aria-activedescendant="listbox-option-3"
      >
        <span v-if="users.length > 0">
          <li
            v-for="user in shownUsers"
            :key="user.id"
            id="listbox-option-0"
            role="option"
            @click="toggleUser(user)"
          >
            <span v-if="selectedUsersString.includes(user.name)">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                />
              </svg>
            </span>
            <span> {{ user.name }} </span>

            <span
              class="part-of-label"
              v-if="
                !selectedUsersString.includes(user.name) && user.group
              "
            >
              {{ lang.part_of }} {{ user.group.name }}
            </span>
          </li>
        </span>
        <span v-else>
          <li id="listbox-option-0" role="option">
            <span>Loading...</span>
          </li>
        </span>
      </ul>
    </div>
  </div>
</template>

<script>
import axios from "axios";
let handleOutsideClick;
export default {
  props: {
    label: {
      type: String,
      required: true,
    },
    lang: {
      type: Object,
      required: true,
    },
    exsistingUsers: {
      type: Array,
      default: () => [],
    },
  },
  directives: {
    clickAway: {
      // for click away events
      bind(el, binding, vnode) {
        // Here's the click/touchstart handler
        // (it is registered below)
        handleOutsideClick = (e) => {
          e.stopPropagation();
          // Get the handler method name and the exclude array
          // from the object used in v-closable
          const { handler, exclude } = binding.value;
          // This variable indicates if the clicked element is excluded
          let clickedOnExcludedEl = false;
          exclude.forEach((refName) => {
            // We only run this code if we haven't detected
            // any excluded element yet
            if (!clickedOnExcludedEl) {
              clickedOnExcludedEl = e.target.classList.value.includes(refName);
            }
          });
          // We check to see if the clicked element is not
          // the dialog element and not excluded
          if (!el.contains(e.target) && !clickedOnExcludedEl) {
            // If the clicked element is outside the dialog
            // and not the button, then call the outside-click handler
            // from the same component this directive is used in
            vnode.context[handler]();
          }
        };
        // Register click/touchstart event listeners on the whole page
        document.addEventListener("click", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);
      },
      unbind() {
        // If the element that has v-closable is removed, then
        // unbind click/touchstart listeners from the whole page
        document.removeEventListener("click", handleOutsideClick);
        document.removeEventListener("touchstart", handleOutsideClick);
      },
    },
  },
  computed: {
    shownUsers() {
      if (this.searchUsers === "") {
        return this.users;
      }

      return this.users.filter((user) => {
        return user.name
          .toLowerCase()
          .includes(this.searchUsers.toLowerCase());
      });
    },
    selectedUsersString() {
      let users = this.selectedUsers;
      if (!users.length || users.length === 0) {
        return "";
      }
      let userNames = [];
      users.forEach((user) => {
        userNames.push(user.name);
      });

      return userNames.join(", ");
    },
  },
  data() {
    return {
      showList: false,
      users: [],
      selectedUsers: [],
      searchUsers: "",
    };
  },
  created() {
    this.selectedUsers = this.exsistingUsers;
  },
  methods: {
    closeUserList() {
      this.showList = false;
      this.searchUsers = "";
    },
    toggleUserList(e) {
      if (e && e.target === this.$refs.searchInput) {
        return;
      }
      this.showList = !this.showList;
      this.searchUsers = "";
      if (this.users.length === 0) {
        axios
          .get("/users/all")
          .then((res) => {
            this.users = res.data;
          })
          .catch((e) => console.error(e));
      }

      this.$nextTick(() => {
        this.$refs.searchInput.focus();
      });
    },

    toggleUser(user) {
      if (
        this.selectedUsers.findIndex((x) => x.name === user.name) != -1
      ) {
        let index = this.selectedUsers.findIndex(
          (x) => x.name === user.name
        );
        return this.selectedUsers.splice(index, 1);
      }

      return this.selectedUsers.push(user);
    },
  },
};
</script>

<style lang="scss" scoped>
button {
  background-image: none;
  text-transform: none;
  appearance: button;
  font-size: 0.875rem;
  line-height: 1.25rem;
  box-shadow: 0 0 #0000, 0 0 #0000, 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  padding-right: 2.5rem;
  padding-left: 0.75rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  background-color: #fff;
  border: 1px solid rgb(209, 213, 219);
  width: 100%;
  position: relative;
  border-radius: 0.375rem;

  .selected-users {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    text-align: left;
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;

    &:focus {
      border: none !important;
      outline: none !important;
    }
  }

  .arrows {
    padding-right: 0.5rem;
    align-items: center;
    display: flex;
    right: 0;
    top: 0;
    bottom: 0;
    position: absolute;
    pointer-events: none;

    svg {
      color: rgb(156, 163, 175);
      width: 1.25rem;
      height: 1.25rem;
      display: block;
      vertical-align: middle;
    }
  }
}

.users-list {
  list-style: none;
  margin: 0;
  padding: 0;
  font-size: 0.875rem;
  line-height: 1.25rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  background-color: white;
  overflow: auto;
  width: 100%;
  max-height: 15rem !important;
  margin-top: 0.25rem;
  z-index: 10;
  position: absolute;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);

  li {
    color: rgb(17, 24, 39);
    padding-right: 2.25rem;
    padding-left: 0.75rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    position: relative;
    display: flex;
    cursor: pointer;

    &:hover {
      background-color: #eee;
    }

    span {
      font-weight: 400;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &.selected {
        font-weight: 600;
      }
    }

    svg {
      color: #0d6efd;
      width: 1.25rem;
      height: 1.25rem;
      display: block;
      vertical-align: middle;
      margin-right: 0.5rem;
    }
  }
}

.list-title {
  position: relative;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  display: inline-flex;
  margin-bottom: 5px;
}

.select-container {
  position: relative;
}

.part-of-label {
  position: absolute;
  right: 1rem;
  opacity: 0.6;
  font-size: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 700;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  border-radius: 0.25rem;
  display: inline-block;
  padding: 0.25em 0.4em;
  color: #fff;
  background-color: #6c757d;
}
</style>